<template>
  <div
    class="relative h-[40px] flex items-center"
    :class="{
      passed: isDone,
      start: overrideWidth > 0
    }"
  >
    <input
      :value="percent"
      class="slider-verify"
      :class="{
        'verify--active': isActive,
        start: overrideWidth > 0,
        done: isDone
      }"
      type="range"
      min="0"
      max="100"
      @mousedown="onSliderTouchStart"
      @mousemove="onSliderMove"
      @mouseup="onSliderTouchEnd"
      @change="onSliderChange"
    >

    <div
      class="slider-override"
      :style="`width: ${overrideWidth}px`"
    >
      <span>{{ placeholderLabel }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ElMessage } from 'element-plus';
import { ref, computed } from 'vue';

const emit = defineEmits(['done', 'cancel']);

const props = defineProps({
  /**
   * 滑块宽度
   * @default 360
   */
  width: {
    type: Number,
    default: 360,
  },
  /**
   * 滑块正常状态的背景色
   * @default #F2F5FA
  */
  defaultBg: {
    type: String,
    default: '#F2F5FA',
  },
  /**
   * 滑块完成状态的背景色
   * @default #67c23a
  */
  successBg: {
    type: String,
    default: '#67c23a',
  },
  inDialog: {
    type: Boolean,
    default: false,
  },
  placeholderLabel: {
    type: String,
    default: '按住滑块，拖到最右边，可发送验证码',
  },
});

let startX = 0;
let endX = 0;
const passvalue = 90;

const percent = ref(0);
const overrideWidth = ref(0);

const containerWidth = computed(() => `${props.width}px`);
const isActive = computed(() => Number(percent.value) >= passvalue);
const isDone = computed(() => Number(percent.value) >= 100);

const onSliderChange = (e) => {
  percent.value = e.target.value;

  if (startX === endX || startX > 100) {
    reset();
    ElMessage.error('请从最左侧拖动滑块');
    e.preventDefault();
    return;
  }

  if (Number(percent.value) >= passvalue) {
    percent.value = 100;
    startX = 0;
    emit('done');
  } else {
    reset();
  }
};

const reset = () => {
  startX = 0;
  percent.value = 0;
  overrideWidth.value = 0;
  emit('cancel');
};

const onSliderMove = (e) => {
  const val = e.target.value;

  overrideWidth.value = Number(val) / 100 * props.width;
};

const onSliderTouchStart = (e) => {
  const parent = e.target.parentElement;
  const topParent = props.inDialog ? parent.offsetParent : null;

  startX = e.pageX - parent.offsetLeft;

  if (topParent) {
    startX = e.pageX - topParent.offsetLeft - parent.offsetLeft - 4;
  }
};

const onSliderTouchEnd = (e) => {
  const parent = e.target.parentElement;
  const topParent = props.inDialog ? parent.offsetParent : null;

  endX = e.pageX - parent.offsetLeft;

  if (topParent) {
    endX = e.pageX - topParent.offsetLeft - parent.offsetLeft - 4;
  }
};

defineExpose({ reset });
</script>

<style lang="scss" scoped>
.slider-verify {
  width: v-bind(containerWidth);
  height: 40px;
  background-color: v-bind(defaultBg);
  outline: none;
  overflow: hidden;
  border-radius: 2px;
  box-sizing: border-box;
  padding: 0 4px;
  position: relative;
  appearance: none;

  &::-moz-range-thumb {
    position: relative;
    z-index: 10;
    cursor: pointer;
    appearance: none;
    width: 32px;
    height: 32px;
    border-radius: 2px;
    background-color: #fff;
    border: 1px solid #B8BFC3;
    background-size: 14px;
    background-repeat: no-repeat;
    background-position: 50%;
    background-image: url(./right-arrow.png);
  }

  &::-webkit-slider-thumb {
    position: relative;
    z-index: 10;
    cursor: pointer;
    appearance: none;
    width: 32px;
    height: 32px;
    border-radius: 2px;
    background-color: #fff;
    border: 1px solid #B8BFC3;
    background-size: 14px;
    background-repeat: no-repeat;
    background-position: 50%;
    background-image: url(./right-arrow.png);
  }

  &.start {
    &::before {
      color: #fff;
    }
  }

  &.verify--active {
    background-color: v-bind(successBg);
    z-index: 12;

    &::before {
      content: '';
    }

    &::-moz-range-thumb {
      border: 1px solid #fff;
      background-size: 16px;
      background-image: url(./circle-tick.png);
    }

    &::-webkit-slider-thumb {
      border: 1px solid #fff;
      background-size: 16px;
      background-image: url(./circle-tick.png);
    }
  }

  &.done {
    pointer-events: none;
  }
}

.slider-override {
  position: absolute;
  top: 0;
  width: 0;
  max-width: v-bind(containerWidth);
  height: 40px;
  background-color: v-bind(successBg);
  border-radius: 2px;

  span {
    content: '按住滑块，拖到最右边，可发送验证码';
    display: inline-block;
    color: #b8bfc3;
    font-size: 14px;
    width: 250px;
    position: absolute;
    top: 11px;
    left: 63px;
    z-index: 1;
  }

  &::after {
    content: '验证通过';
    position: absolute;
    display: none;
    color: #fff;
    top: 10px;
    left: 50%;
    z-index: 12;
    font-size: 14px;
    margin-left: -28px;
  }
}

.start {
  .slider-override {
    span {
      color: #fff;
    }
  }
}

.passed {
  .slider-override {
    span {
      display: none;
    }

    &::after {
      display: inline-block;
    }
  }
}
</style>
